import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import {
  DEFAULT_PAGE,
  DEFAULT_COUNT,
  SORT_COLUMN_DATE,
  SORT_COLUMN_VIEW,
} from "@/constants/posts";

const SERVER_URL = process.env.NEXT_PUBLIC_SERVER_URL;

if (!SERVER_URL) {
  throw new Error("NEXT_PUBLIC_SERVER_URL 환경변수가 설정되지 않았습니다.");
}

export interface PostsListParams {
  page?: number;
  count?: number;
  search?: string;
  sort?: string;
  siteId?: number;
  isHidden?: boolean;
}

export interface PostsListResponse {
  result: boolean;
  data: {
    total: number;
    list: PostItem[];
  };
}

export interface PostItem {
  id: number;
  title: string;
  content: string;
  thumbnailUrl: string | null;
  view: number;
  isHidden: boolean;
  createDate: string;
}

export interface PostAdjacentItem {
  id: number;
  title: string;
}

export interface PostDetailData extends PostItem {
  prv: PostAdjacentItem | null;
  next: PostAdjacentItem | null;
}

export interface PostDetailResponse {
  result: boolean;
  data: PostDetailData;
}

/** 게시글 목록 fetch */
const fetchPostsList = async (params: PostsListParams = {}): Promise<PostsListResponse> => {
  const { page = DEFAULT_PAGE, count = DEFAULT_COUNT, sort, ...rest } = params;
  const sortColumn = sort ? SORT_COLUMN_VIEW : SORT_COLUMN_DATE;

  const res = await axios.get<PostsListResponse>(`${SERVER_URL}article/public`, {
    params: {
      page,
      count,
      sortColumn,
      sort,
      ...rest,
    },
  });
  return res.data;
};

/** 게시글 상세 fetch */
const fetchPostDetail = async (id: string, siteId: number): Promise<PostDetailResponse> => {
  const res = await axios.get<PostDetailResponse>(`${SERVER_URL}article/public/${id}/site/${siteId}`);
  return res.data;
};

/** 게시글 목록 조회 Hook */
export const useGetPostsList = (params: PostsListParams = {}) => {
  return useQuery({
    queryKey: ["posts", "list", params],
    queryFn: () => fetchPostsList(params),
  });
};

/** 게시글 상세 조회 Hook */
export const useGetPostDetail = (id: string, siteId: number) => {
  return useQuery({
    queryKey: ["posts", "detail", id, siteId],
    queryFn: () => fetchPostDetail(id, siteId),
    enabled: !!id && !!siteId,
  });
};
